import { Avatar, Box, Button, Card, CircularProgress, TextField, Typography, useMediaQuery } from '@mui/material';
import { ChangeEvent, useContext, useEffect, useRef, useState } from 'react';
import Message from './Message.tsx';
import { useTranslation } from 'react-i18next';
import { Attachment, Cancel, DoneAllRounded, Send } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import axios from 'axios';
import Dialog from '@mui/material/Dialog';
import FileChat from './FileChat.tsx';
import { IMessageChat, ITicketChat, STATUS_CHAT } from '../../../interfaces/chat.ts';
import { UserContext } from '../../../contexts/UserContext.tsx';
import ChatLayout from './ChatLayout.tsx';

const PAGE_SIZE = 10;

export default function Chat({ id, layout, type }: { id: number; layout: boolean; type?: 'admin' | 'user' }) {
	const { user } = useContext(UserContext);

	const isSmallScreen = useMediaQuery((theme: any) => theme.breakpoints.down('md'));

	const { t } = useTranslation();

	const [data, setData] = useState<IMessageChat[]>([]);
	const [audience, setAudience] = useState<ITicketChat>();
	const [text, setText] = useState('');
	const [file, setFile] = useState<File>();
	const [total, setTotal] = useState(1);
	const [page, setPage] = useState(1);
	const [lock, setLock] = useState(true);
	const [showLayout, setShowLayout] = useState(layout && !isSmallScreen);

	const fileRef = useRef<HTMLInputElement>(null);

	const closeLayout = () => setShowLayout(false);

	const openLayout = () => setShowLayout(true);

	const getMessages = () => {
		axios({
			method: 'get',
			url: type === 'user' ? `/tickets/messages/${id}` : `/admin/tickets/messages/${id}`,
			params: {
				page: page,
				limit: PAGE_SIZE,
			},
		})
			.then(r => {
				if (r?.data?.success) {
					setData(p => [...p, ...r?.data?.data]);
					setTotal(r?.data?.pagination?.total);
				}
			})
			.finally(() => {
				setLock(false);
			});
	};

	useEffect(() => {
		getMessages();
	}, [page]);

	useEffect(() => {
		axios({ method: 'get', url: user?.is_admin ? `admin/tickets/${id}` : `/tickets/${id}`, loading: true }).then(r => {
			if (r?.data?.success) {
				setAudience(r?.data?.data);
			}
		});
	}, []);

	const clickInputFile = () => {
		if (fileRef.current) {
			fileRef.current.value = '';
		}

		fileRef?.current?.click();
	};

	const changeText = (e: ChangeEvent<HTMLInputElement>) => {
		setText(e?.target?.value);
	};

	const keyDown = (e: unknown) => {
		if (!e) return;

		// @ts-ignore
		if (e.key === 'Enter' && text && !e?.shiftKey) {
			sendMessage();
			// todo: if error. dont new line
		}
	};

	const changeFile = (event: React.ChangeEvent<HTMLInputElement>) => {
		const currentFile = event?.target?.files?.[0];
		if (currentFile) setFile(currentFile);
	};

	const removeFile = () => {
		if (fileRef.current) {
			fileRef.current.value = '';
		}
		setFile(undefined);
	};

	const sendMessage = () => {
		if (!text && !file) return;
		const newData = new FormData();

		newData.append('ticket_id', id + '');
		newData.append('message', text.trim() ?? '');

		if (file) newData.append('file', file);

		axios.post(type === 'user' ? `tickets/send` : 'admin/tickets/send', newData, { loading: true }).then(r => {
			if (r?.data?.success) {
				setData(p => [r?.data?.data, ...p]);
				setText('');
				setFile(undefined);
			}
		});
	};

	const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
		const target = e.target as HTMLDivElement;

		if (!lock && data?.length < total && target.scrollHeight - 50 < target.clientHeight - target.scrollTop) {
			setLock(true);
			setPage(page + 1);
		}
	};

	const textInput = <TextField autoComplete={'off'} value={text} onChange={changeText} multiline placeholder={isSmallScreen ? t('message...') : t('type_your_message_here')} autoFocus onKeyDown={keyDown} className={'-mb-1 grow overflow-auto'} sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { border: 'none' } } }} />;

	const sendButton = isSmallScreen ? (
		<IconButton onClick={sendMessage} disabled={!text && !file}>
			<Send color={text ? 'primary' : undefined} />
		</IconButton>
	) : (
		<Button disabled={!text && !file} startIcon={<Send />} className={'h-9 md:w-28'} onClick={sendMessage}>
			{t('send_message')}
		</Button>
	);

	return (
		<>
			<Dialog open={!!file} onClose={removeFile} fullWidth maxWidth={'md'} sx={{ '& .MuiPaper-root': { overflow: 'visible' } }}>
				<Box className={'relative flex flex-col gap-3 p-4 pb-6'}>
					<IconButton className="absolute -right-4 -top-4 z-50 bg-background-default p-0" onClick={removeFile}>
						<Cancel fontSize={'large'} />
					</IconButton>
					<Box className={'rounded bg-grey-400'}>
						<FileChat file={file} />
					</Box>
					<Box className="flex">
						{textInput}
						{sendButton}
					</Box>
				</Box>
			</Dialog>

			{layout && (
				<Card className={`flex flex-col justify-between ${isSmallScreen ? 'absolute z-10' : ''} ${isSmallScreen ? (showLayout ? 'w-full p-6' : 'w-0 px-0') : showLayout ? 'basis-1/3 p-3' : 'basis-0 px-0'} h-full pb-10 transition-all`}>
					<ChatLayout closeLayout={closeLayout} audience={audience} setAudience={setAudience} id={id} />
				</Card>
			)}

			<Card className={`${isSmallScreen ? 'w-full' : showLayout ? 'basis-2/3' : 'basis-full'} rounded transition-all`}>
				<Box className={'h-[calc(100vh-150px)]'}>
					<Box className={'flex cursor-pointer gap-4 rounded-t bg-primary-focus px-6 py-4'} onClick={openLayout}>
						<Avatar src={user?.image ?? ''} className={'shadow'} />
						<Box className="flex flex-col">
							<Typography className={'text-grey-900'}>{audience?.title}</Typography>
							<Typography className={'text-sm text-grey-600'}>{audience?.department}</Typography>
						</Box>
					</Box>
					<Box className={`relative h-[calc(100%-80px)] ${audience?.status !== STATUS_CHAT.Closed ? 'pb-24' : ''}`}>
						<Box className={'flex max-h-full flex-col-reverse overflow-y-auto p-6'} onScroll={handleScroll}>
							{data?.map((mess, index) => (
								<>
									<Box className={`flex gap-4 ${mess?.is_user_message ? 'flex-row-reverse' : ''} ${(data?.length > index + 1 && data?.[index + 1]?.is_user_message === mess?.is_user_message) || data?.length === index + 1 ? 'mt-1.5' : 'mt-12'}`}>
										{data?.length <= index + 1 || data?.[index + 1]?.is_user_message !== mess?.is_user_message ? <Avatar src={''} className={'sticky top-3'} /> : <div className={'h-10 w-10'} />}
										<Box className={`flex flex-col ${mess?.is_user_message ? 'items-end' : 'items-start'}`}>
											<Message message={mess} />
											{(index === 0 || data?.[index - 1]?.is_user_message !== mess?.is_user_message) && (
												<Box className={'flex flex-row-reverse items-center gap-2'}>
													{user?.is_admin === mess?.is_user_message && <DoneAllRounded color={'success'} fontSize={'small'} />}
													<Typography className={'mt-1.5 text-grey-500'}>{`${new Date(mess?.created_at).getHours()}:${new Date(mess?.created_at).getMinutes()}`}</Typography>
												</Box>
											)}
										</Box>
									</Box>
									{(data?.length === index + 1 || new Date(data?.[index + 1]?.created_at).toLocaleDateString('fa-IR') !== new Date(data?.[index]?.created_at).toLocaleDateString('fa-IR')) && (
										<Box className={'flex w-full justify-center py-3'}>
											<Typography className={'rounded-full bg-gray-300 px-3 py-1'}>
												{new Date(data?.[index]?.created_at).toLocaleDateString('fa-IR', {
													month: 'long',
													day: 'numeric',
												})}
											</Typography>
										</Box>
									)}
								</>
							))}
							{data?.length < total && (
								<Box className="mb-4 flex justify-center">
									<CircularProgress />
								</Box>
							)}
						</Box>
						{audience?.status !== STATUS_CHAT.Closed && (
							<Box className={'absolute bottom-0 w-full px-4 py-6 md:pe-16 md:ps-7'}>
								<Box className={'flex max-h-64 w-full items-end gap-1 overflow-y-auto rounded-md bg-white px-2 pb-3 pt-3 shadow md:gap-2 md:px-4'}>
									{textInput}
									{sendButton}
									<IconButton className={'h-9 w-9 -rotate-45'} onClick={clickInputFile}>
										<Attachment />
										<input type={'file'} hidden ref={fileRef} onChange={changeFile} />
									</IconButton>
								</Box>
							</Box>
						)}
					</Box>
				</Box>
			</Card>
		</>
	);
}
